import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { AgentCommandRecord, ProvisioningStepRecord, ServerDetail } from "@hostdock/types";
import {
  ActionMenu, Badge, Button, ConfirmDialog, DataTable, Dialog, EmptyState, ErrorState, Field, formatBytes, formatDateTime, formatDuration,
  formatRate, formatRelative, Input, KeyValue, LineChart, Notice, PageHeader, Panel, Select, Skeleton, Spinner, Stat, StatGroup, StatusBadge,
  Tabs, toast,
} from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useLiveJob } from "../../lib/use-job";
import { useSession } from "../../session";
import { ServerStatusBadge } from "./shared";

type Tab = "overview" | "monitoring" | "provisioning" | "readiness" | "events";
type Pending = null | "maintenance-on" | "maintenance-off" | "drain-on" | "drain-off" | "reboot" | "update" | "retire" | "remove" | "rotate" | "logs";

export function ServerDetailPage() {
  return (
    <RequirePermission anyOf={["INFRASTRUCTURE_VIEW"]}>
      <ServerView />
    </RequirePermission>
  );
}

function ServerView() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "overview";
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["server", id], queryFn: () => api.servers.get(id), refetchInterval: 10_000 });
  const [pending, setPending] = useState<Pending>(null);
  const [diag, setDiag] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["server", id] });
    void qc.invalidateQueries({ queryKey: ["server-provisioning", id] });
    void qc.invalidateQueries({ queryKey: ["servers"] });
  };
  const done = (msg: string) => {
    toast.success(msg);
    setPending(null);
    refresh();
  };
  const provision = useMutation({ mutationFn: () => api.servers.provision(id), onSuccess: () => { done("Provisioning restarted"); setParams({ tab: "provisioning" }); } });
  const update = useMutation({ meta: { inlineError: true }, mutationFn: () => api.servers.updateComponents(id), onSuccess: () => { done("Update started"); setParams({ tab: "provisioning" }); } });
  const activate = useMutation({ mutationFn: () => api.servers.activate(id), onSuccess: () => done("Server activated") });
  const maintenance = useMutation({ meta: { inlineError: true }, mutationFn: (v: { enabled: boolean; reason: string }) => api.servers.maintenance(id, v.enabled, v.reason), onSuccess: (s) => done(s.status === "MAINTENANCE" ? "Maintenance mode on" : "Maintenance mode off") });
  const drain = useMutation({ meta: { inlineError: true }, mutationFn: (v: { enabled: boolean; reason: string }) => api.servers.drain(id, v.enabled, v.reason), onSuccess: (s) => done(s.status === "DRAINING" ? "Server draining" : "Server back in service") });
  const reboot = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.servers.command(id, { type: "REBOOT", reason }), onSuccess: () => done("Reboot requested") });
  const rotate = useMutation({ meta: { inlineError: true }, mutationFn: () => api.servers.rotateAgent(id), onSuccess: () => done("Rotation requested; applied at the next heartbeat") });
  const retire = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.servers.retire(id, reason), onSuccess: () => done("Server retired") });
  const remove = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.servers.remove(id, q.data!.name),
    onSuccess: () => {
      toast.success("Server removed");
      void qc.invalidateQueries({ queryKey: ["servers"] });
      navigate("/infrastructure/servers");
    },
  });
  const diagnostics = useMutation({ mutationFn: () => api.servers.command(id, { type: "READINESS" }), onSuccess: (c) => setDiag(c.id) });

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const s = q.data;
  const agentActive = s?.agent?.status === "ACTIVE";
  const live = s && ["READY", "ACTIVE", "MAINTENANCE", "DRAINING"].includes(s.status);

  return (
    <>
      <PageHeader
        title={s ? <span className="font-mono">{s.name}</span> : <Skeleton className="h-6 w-48" />}
        description={s ? `${s.region.name} · ${s.publicIp}${s.hostname ? ` · ${s.hostname}` : ""}` : undefined}
        meta={s && (
          <div className="flex flex-wrap items-center gap-2">
            <ServerStatusBadge status={s.displayStatus} />
            {s.statusReason && <span className="text-[12.5px] text-muted">{s.statusReason}</span>}
          </div>
        )}
        actions={s && (
          <>
            {["NEW", "FAILED"].includes(s.status) && can("SERVER_PROVISION") && <Button variant="primary" icon={<RefreshCw size={14} />} loading={provision.isPending} onClick={() => provision.mutate()}>Retry provisioning</Button>}
            {s.status === "READY" && can("SERVER_MANAGE") && <Button variant="primary" loading={activate.isPending} onClick={() => activate.mutate()}>Activate</Button>}
            {agentActive && live && can("SERVER_MANAGE") && <Button loading={diagnostics.isPending} onClick={() => diagnostics.mutate()}>Diagnostics</Button>}
            {agentActive && can("LOGS_VIEW") && <Button onClick={() => setPending("logs")}>Logs</Button>}
            <ActionMenu
              trigger={<Button aria-label="More actions">More</Button>}
              actions={[
                { label: "Enter maintenance", onSelect: () => setPending("maintenance-on"), hidden: !can("SERVER_MANAGE") || !["READY", "ACTIVE"].includes(s.status) },
                { label: "End maintenance", onSelect: () => setPending("maintenance-off"), hidden: !can("SERVER_MANAGE") || s.status !== "MAINTENANCE" },
                { label: "Drain server", onSelect: () => setPending("drain-on"), hidden: !can("SERVER_DRAIN") || s.status !== "ACTIVE" },
                { label: "Stop draining", onSelect: () => setPending("drain-off"), hidden: !can("SERVER_DRAIN") || s.status !== "DRAINING" },
                { label: "Update HostDock components", onSelect: () => setPending("update"), hidden: !can("SERVER_PROVISION") || !live },
                { label: "Rotate agent credentials", onSelect: () => setPending("rotate"), hidden: !can("SERVER_MANAGE") || !agentActive },
                { label: "Reboot", onSelect: () => setPending("reboot"), hidden: !can("SERVER_MANAGE") || !agentActive || s.status === "RETIRED" },
                { label: "Retire", onSelect: () => setPending("retire"), danger: true, hidden: !can("SERVER_REMOVE") || ["RETIRED", "PROVISIONING", "CONNECTING", "TESTING"].includes(s.status) },
                { label: "Remove", onSelect: () => setPending("remove"), danger: true, hidden: !can("SERVER_REMOVE") || !["RETIRED", "NEW", "FAILED"].includes(s.status) },
              ]}
            />
          </>
        )}
      />
      <Tabs<Tab>
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        items={[
          { value: "overview", label: "Overview" },
          { value: "monitoring", label: "Monitoring" },
          { value: "provisioning", label: "Provisioning" },
          { value: "readiness", label: "Readiness" },
          { value: "events", label: "Events" },
        ]}
      />
      {!s ? <Skeleton className="h-64 w-full" /> : tab === "overview" ? <Overview s={s} /> : tab === "monitoring" ? <Monitoring id={id} /> : tab === "provisioning" ? <Provisioning id={id} onChange={refresh} /> : tab === "readiness" ? <Readiness s={s} /> : <Events id={id} />}

      {diag && <CommandDialog serverId={id} commandId={diag} title="Diagnostics" onClose={() => { setDiag(null); refresh(); }} />}
      {pending === "logs" && s && <LogsDialog serverId={id} onClose={() => setPending(null)} />}
      <ConfirmDialog open={pending === "maintenance-on"} onOpenChange={(o) => !o && setPending(null)} title="Enter maintenance mode?" description="No new workloads will be placed on this server. Existing workloads keep running." confirmLabel="Enter maintenance" tone="primary" requireReason loading={maintenance.isPending} error={maintenance.error} onConfirm={(reason) => maintenance.mutate({ enabled: true, reason })} />
      <ConfirmDialog open={pending === "maintenance-off"} onOpenChange={(o) => !o && setPending(null)} title="End maintenance mode?" description="The server returns to ACTIVE and becomes eligible for new workloads." confirmLabel="End maintenance" tone="primary" requireReason loading={maintenance.isPending} error={maintenance.error} onConfirm={(reason) => maintenance.mutate({ enabled: false, reason })} />
      <ConfirmDialog open={pending === "drain-on"} onOpenChange={(o) => !o && setPending(null)} title="Drain this server?" description={`No new workloads will be placed here. The ${s?.hostedWebsites ?? 0} website(s) still hosted keep running and must be moved to another server before it can be retired.`} confirmLabel="Start draining" tone="primary" requireReason loading={drain.isPending} error={drain.error} onConfirm={(reason) => drain.mutate({ enabled: true, reason })} />
      <ConfirmDialog open={pending === "drain-off"} onOpenChange={(o) => !o && setPending(null)} title="Stop draining?" description="The server returns to ACTIVE and becomes eligible for new workloads again." confirmLabel="Stop draining" tone="primary" requireReason loading={drain.isPending} error={drain.error} onConfirm={(reason) => drain.mutate({ enabled: false, reason })} />
      <ConfirmDialog open={pending === "update"} onOpenChange={(o) => !o && setPending(null)} title="Update HostDock components?" description="Re-runs the provisioning pipeline (idempotent) and the production tests. The server is placed in maintenance during the update; if it fails it stays in maintenance." confirmLabel="Start update" tone="primary" loading={update.isPending} error={update.error} onConfirm={() => update.mutate()} />
      <ConfirmDialog open={pending === "rotate"} onOpenChange={(o) => !o && setPending(null)} title="Rotate agent credentials?" description="The agent generates a new key and certificate at its next heartbeat; the old certificate stops working once the new one is used." confirmLabel="Rotate" tone="primary" loading={rotate.isPending} error={rotate.error} onConfirm={() => rotate.mutate()} />
      <ConfirmDialog open={pending === "reboot"} onOpenChange={(o) => !o && setPending(null)} title={`Reboot ${s?.name}?`} description="All workloads on this server will be unavailable until it comes back." confirmLabel="Reboot" confirmText={s?.name} requireReason loading={reboot.isPending} error={reboot.error} onConfirm={(r) => reboot.mutate(r)} />
      <ConfirmDialog open={pending === "retire"} onOpenChange={(o) => !o && setPending(null)} title={`Retire ${s?.name}?`} description="The agent's credentials are revoked immediately and the server is excluded from HostDock. Workload draining arrives with Phase 7; this server has no workloads." confirmLabel="Retire server" confirmText={s?.name} requireReason loading={retire.isPending} error={retire.error} onConfirm={(r) => retire.mutate(r)} />
      <ConfirmDialog open={pending === "remove"} onOpenChange={(o) => !o && setPending(null)} title={`Remove ${s?.name}?`} description="Deletes the server record, its metrics and every stored credential. The audit log keeps the history. This cannot be undone." confirmLabel="Remove server" confirmText={s?.name} loading={remove.isPending} error={remove.error} onConfirm={() => remove.mutate()} />
    </>
  );
}

const CONNECTIVITY: Record<ServerDetail["agentConnectivity"], string> = { AUTO: "Automatic", DIRECT: "Direct only", TUNNEL: "Always SSH tunnel" };

function AgentConnection({ s }: { s: ServerDetail }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(s.agentConnectivity);
  const save = useMutation({
    mutationFn: () => api.servers.update(s.id, { agentConnectivity: value }),
    onSuccess: () => { toast.success(value === "TUNNEL" ? "The control plane is opening the SSH tunnel" : "Saved; applied at the next provisioning or update run"); setOpen(false); void qc.invalidateQueries({ queryKey: ["server", s.id] }); },
  });
  const path = s.agentConnection === "TUNNEL" ? "SSH tunnel" : s.agentConnection === "DIRECT" ? "Direct" : "Not determined yet";
  const t = s.agentTunnel;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>{path}</span>
      {s.agentConnection === "TUNNEL" && (
        <Badge tone={t?.state === "CONNECTED" ? "success" : t?.state === "CONNECTING" ? "warning" : "danger"}>
          {t?.state === "CONNECTED" ? `connected ${formatRelative(t.since)}` : t?.state === "CONNECTING" ? "connecting" : "down"}
        </Badge>
      )}
      <span className="text-muted">· {CONNECTIVITY[s.agentConnectivity]}</span>
      <button type="button" className="text-[12.5px] text-accent hover:underline" onClick={() => { setValue(s.agentConnectivity); setOpen(true); }}>Change</button>
      {t?.state === "DISCONNECTED" && t.error && <span className="w-full text-[12px] text-danger">{t.error}</span>}
      <Dialog open={open} onOpenChange={setOpen} title="Agent connection" size="sm"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={value === s.agentConnectivity} onClick={() => save.mutate()}>Save</Button></>}>
        <Field label="How the agent reaches the control plane" hint="Automatic and Direct are re-tested on the next provisioning or Update run. SSH tunnel applies immediately, but the agent must be re-installed with Update to use it.">
          {(p) => (
            <Select {...p} value={value} onChange={(e) => setValue(e.target.value as ServerDetail["agentConnectivity"])}>
              <option value="AUTO">Automatic (recommended)</option>
              <option value="DIRECT">Direct only</option>
              <option value="TUNNEL">Always use SSH tunnel</option>
            </Select>
          )}
        </Field>
        {save.error ? <p className="mt-3 text-[13px] text-danger">{(save.error as Error).message}</p> : null}
      </Dialog>
    </span>
  );
}

/** Commands a node needs before new WordPress sites are placed on it (see the worker's placement). */
const WORDPRESS_COMMANDS = ["PACKAGE_APPLY", "WP_CLI", "SITE_COPY", "DB_COPY", "SNAPSHOT_CREATE", "SNAPSHOT_RESTORE", "SNAPSHOT_DELETE"];

function Overview({ s }: { s: ServerDetail }) {
  const l = s.latest;
  return (
    <div className="flex flex-col gap-5">
      {s.status === "FAILED" && s.statusReason && <Notice tone="danger" title="Provisioning failed">{s.statusReason}. Fix the cause and use Retry provisioning; completed steps are not repeated.</Notice>}
      {s.agent && s.agent.missingCommands.length > 0 && (
        <Notice tone="warning" title="HostDock Agent update needed">
          This server's agent{s.agent.version ? ` (${s.agent.version})` : ""} cannot run {s.agent.missingCommands.length === 1 ? "one feature" : `${s.agent.missingCommands.length} features`} this control plane uses
          ({s.agent.missingCommands.slice(0, 4).join(", ")}{s.agent.missingCommands.length > 4 ? ", …" : ""}).{" "}
          {s.agent.missingCommands.includes("PACKAGE_APPLY") ? "New websites are not placed here until you run" : s.agent.missingCommands.some((c) => WORDPRESS_COMMANDS.includes(c)) ? "New WordPress sites are not placed here until you run" : "Run"} More → Update HostDock components.
        </Notice>
      )}
      {/* HostDock DNS readiness is shown in Settings → DNS, where nameservers are chosen. */}
      {s.agent && s.agent.featuresNotEnabled.some((f) => f !== "DNS") && (
        <Notice title="Optional features not running on this server">
          {s.agent.featuresNotEnabled.includes("EMAIL") && <>Email hosting is not running. </>}
          {s.agent.featuresNotEnabled.includes("VIRUS_SCAN") && <>Malware scanning (ClamAV) is not running. </>}
          Both are installed automatically by provisioning and by More → Update HostDock components; the &quot;Email hosting&quot; and &quot;Malware scanning&quot; steps of the last run say why they were skipped (usually the hostname&apos;s DNS record or the server&apos;s memory). Fix that and run the update again.
        </Notice>
      )}
      {s.displayStatus === "OFFLINE" && <Notice tone="danger" title="Server offline">No heartbeat from the HostDock Agent since {formatDateTime(s.lastSeenAt)}.</Notice>}
      <StatGroup className="grid-cols-2 lg:grid-cols-6">
        <Stat label="CPU" value={l ? `${l.cpuPercent.toFixed(0)}%` : "—"} hint={s.system.cpuCores ? `${s.system.cpuCores} cores` : undefined} />
        <Stat label="RAM" value={l ? `${formatBytes(l.memUsed)}` : "—"} hint={l ? `of ${formatBytes(l.memTotal)}` : undefined} />
        <Stat label="Disk" value={l ? formatBytes(l.diskUsed) : "—"} hint={l ? `of ${formatBytes(l.diskTotal)}` : undefined} />
        <Stat label="Load" value={l ? l.load1.toFixed(2) : "—"} />
        <Stat label="Containers" value={l ? l.containersRunning : "—"} />
        <Stat label="Uptime" value={formatDuration(s.system.uptimeSeconds)} />
      </StatGroup>
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Services" flush>
          <DataTable
            rows={s.services}
            rowKey={(x) => x.service}
            skeletonRows={4}
            empty={<EmptyState title="No service data yet" description="The agent reports services after it is installed." />}
            columns={[
              { key: "svc", header: "Service", cell: (x) => <span className="font-medium">{SERVICE_LABEL[x.service] ?? x.service}</span> },
              { key: "st", header: "Status", cell: (x) => <StatusBadge status={x.status === "RUNNING" ? "ACTIVE" : x.status === "NOT_INSTALLED" ? "DISABLED" : x.status} className="" /> },
              { key: "d", header: "Detail", cell: (x) => <span className="text-[12px] text-muted">{x.detail ?? ""}</span>, hideOnMobile: true },
            ]}
          />
        </Panel>
        <Panel title="System">
          <KeyValue
            items={[
              { label: "Operating system", value: s.system.osName ? `${s.system.osName} ${s.system.osVersion ?? ""}` : "—" },
              { label: "Kernel", value: s.system.kernel ?? "—" },
              { label: "Architecture", value: s.system.arch ?? "—" },
              { label: "SSH", value: <span className="font-mono text-[12.5px]">{s.sshUser}@{s.publicIp}:{s.sshPort}</span> },
              { label: "Host key", value: <span className="font-mono text-[11.5px] break-all">{s.hostKeyFingerprint ?? "—"}</span> },
              { label: "Agent", value: s.agent ? `${s.agent.status === "ACTIVE" ? "Enrolled" : s.agent.status.toLowerCase().replace("_", " ")}${s.agent.version ? ` · ${s.agent.version}` : ""}` : "Not installed" },
              { label: "Agent connection", value: <AgentConnection s={s} /> },
              { label: "Agent certificate", value: s.agent?.certNotAfter ? `Valid until ${formatDateTime(s.agent.certNotAfter)}${s.agent.rotationRequested ? " (rotation pending)" : ""}` : "—" },
              { label: "Last heartbeat", value: s.lastSeenAt ? formatRelative(s.lastSeenAt) : "Never" },
              { label: "Production ready", value: s.productionReadyAt ? formatDateTime(s.productionReadyAt) : "Not yet" },
              { label: "Hosted websites", value: s.hostedWebsites },
              { label: "MongoDB", value: s.mongodbEnabled ? "Enabled" : "Disabled" },
              { label: "Labels", value: Object.keys(s.labels).length ? <div className="flex flex-wrap gap-1">{Object.entries(s.labels).map(([k, v]) => <Badge key={k}>{k}={v}</Badge>)}</div> : "—" },
            ]}
          />
        </Panel>
      </div>
    </div>
  );
}

const SERVICE_LABEL: Record<string, string> = {
  agent: "HostDock Agent", docker: "Docker", proxy: "Reverse proxy", mariadb: "MariaDB", postgresql: "PostgreSQL", redis: "Redis",
  mongodb: "MongoDB", phpmyadmin: "phpMyAdmin", sftp: "SFTP", monitoring: "Monitoring", logging: "Logging", backup: "Backup agent",
};

const RANGES = ["1h", "6h", "24h", "7d", "30d"] as const;

function Monitoring({ id }: { id: string }) {
  const [range, setRange] = useState<(typeof RANGES)[number]>("1h");
  const q = useQuery({ queryKey: ["server-metrics", id, range], queryFn: () => api.servers.metrics(id, range), refetchInterval: 60_000 });
  const pts = q.data?.points ?? [];
  const times = pts.map((p) => p.t);
  const pct = (v: number) => `${v.toFixed(0)}%`;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1" role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <Button key={r} size="sm" variant={r === range ? "primary" : "ghost"} onClick={() => setRange(r)}>{r}</Button>
        ))}
      </div>
      {q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          <Panel><LineChart title="Utilisation" times={times} max={100} format={pct} series={[
            { key: "cpu", label: "CPU", slot: 1, values: pts.map((p) => p.cpuPercent) },
            { key: "mem", label: "Memory", slot: 2, values: pts.map((p) => p.memPercent) },
            { key: "disk", label: "Disk", slot: 3, values: pts.map((p) => p.diskPercent) },
          ]} /></Panel>
          <Panel><LineChart title="Load average (1 min)" times={times} format={(v) => v.toFixed(2)} series={[{ key: "load", label: "Load", slot: 1, values: pts.map((p) => p.load1) }]} /></Panel>
          <Panel><LineChart title="Network" times={times} format={formatRate} series={[
            { key: "rx", label: "Received", slot: 1, values: pts.map((p) => p.netRxBps) },
            { key: "tx", label: "Sent", slot: 2, values: pts.map((p) => p.netTxBps) },
          ]} /></Panel>
          <Panel><LineChart title="Disk I/O" times={times} format={formatRate} series={[
            { key: "rd", label: "Read", slot: 1, values: pts.map((p) => p.diskReadBps) },
            { key: "wr", label: "Write", slot: 2, values: pts.map((p) => p.diskWriteBps) },
          ]} /></Panel>
          <Panel><LineChart title="Inode usage" times={times} max={100} format={pct} series={[{ key: "ino", label: "Inodes", slot: 1, values: pts.map((p) => p.inodePercent) }]} /></Panel>
        </div>
      )}
    </div>
  );
}

const STEP_ICON: Record<string, string> = { SUCCESS: "text-success", FAILED: "text-danger", RUNNING: "text-info", SKIPPED: "text-subtle", PENDING: "text-subtle" };

function Provisioning({ id, onChange }: { id: string; onChange(): void }) {
  const q = useQuery({
    queryKey: ["server-provisioning", id],
    queryFn: () => api.servers.provisioning(id),
    refetchInterval: (query) => (["RUNNING", "PENDING"].includes(query.state.data?.latest?.status ?? "") ? 3000 : false),
  });
  const run = q.data?.latest;
  const lastStatus = useRef<string | undefined>(undefined);
  // Live updates: the background job streams progress; refetch steps on every event.
  useLiveJob(run && ["RUNNING", "PENDING"].includes(run.status) ? run.backgroundJobId : null, () => void q.refetch());
  useEffect(() => {
    if (run && lastStatus.current && lastStatus.current !== run.status) onChange();
    lastStatus.current = run?.status;
  }, [run, onChange]);
  const [open, setOpen] = useState<string | null>(null);
  const steps = run?.steps ?? [];
  useAutoOpen(steps.find((x) => x.status === "FAILED") ?? steps.find((x) => x.status === "RUNNING"), open, setOpen);

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isPending) return <Skeleton className="h-96 w-full" />;
  if (!run) return <EmptyState title="No provisioning runs" />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={run.status} />
        <span className="text-[13px] text-muted">
          {run.kind === "UPDATE" ? "Component update" : "Provisioning"} · {run.progress.done}/{run.progress.total} steps
          {run.startedAt && ` · started ${formatRelative(run.startedAt)}`}
          {run.finishedAt && ` · finished ${formatRelative(run.finishedAt)}`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={run.progress.done} aria-valuemax={run.progress.total}>
        <div className={`h-full transition-all ${run.status === "FAILED" ? "bg-danger" : "bg-accent"}`} style={{ width: `${(run.progress.done / run.progress.total) * 100}%` }} />
      </div>
      {run.error && <Notice tone="danger" title="Run failed">{run.error}</Notice>}
      <Panel flush>
        <ol>
          {steps.map((st) => (
            <StepRow key={st.key} step={st} open={open === st.key} onToggle={() => setOpen(open === st.key ? null : st.key)} />
          ))}
        </ol>
      </Panel>
    </div>
  );
}

/** Expands the running or failed step once, without fighting the user's own toggling. */
function useAutoOpen(step: ProvisioningStepRecord | undefined, open: string | null, setOpen: (k: string | null) => void) {
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (step && done.current !== `${step.key}:${step.status}` && open === null) {
      done.current = `${step.key}:${step.status}`;
      setOpen(step.key);
    }
  }, [step, open, setOpen]);
}

function StepRow({ step, open, onToggle }: { step: ProvisioningStepRecord; open: boolean; onToggle(): void }) {
  const logRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (open && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [open, step.logs]);
  const dur = step.startedAt && step.finishedAt ? Math.max(0, (new Date(step.finishedAt).getTime() - new Date(step.startedAt).getTime()) / 1000) : null;
  return (
    <li className="border-b border-line last:border-0">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-2/60" aria-expanded={open}>
        {open ? <ChevronDown size={14} className="text-subtle" /> : <ChevronRight size={14} className="text-subtle" />}
        <span className={`w-5 text-center ${STEP_ICON[step.status]}`} aria-hidden>
          {step.status === "SUCCESS" ? "✓" : step.status === "FAILED" ? "✕" : step.status === "RUNNING" ? "●" : step.status === "SKIPPED" ? "–" : "○"}
        </span>
        <span className="flex-1 text-[13.5px] font-medium">{step.name}</span>
        {step.retryCount > 0 && <Badge tone="warning">retried {step.retryCount}×</Badge>}
        <span className="w-24 text-right text-[12px] text-muted">{step.status === "RUNNING" ? <Spinner label="" /> : dur !== null ? `${dur < 60 ? `${dur.toFixed(0)}s` : `${(dur / 60).toFixed(1)}m`}` : ""}</span>
        <StatusBadge status={step.status} />
      </button>
      {open && (
        <div className="border-t border-line bg-surface-2/40 px-4 py-3">
          {step.error && <p className="mb-2 text-[13px] text-danger">{step.error}</p>}
          <pre ref={logRef} className="max-h-80 overflow-auto rounded-md border border-line bg-surface p-3 font-mono text-[11.5px] whitespace-pre-wrap">{step.logs || "No output yet."}</pre>
        </div>
      )}
    </li>
  );
}

function Readiness({ s }: { s: ServerDetail }) {
  const failed = s.readiness.filter((c) => c.status === "FAIL").length;
  return (
    <Panel title="Production readiness" description={s.readiness.length ? `${s.readiness.length} checks · last run ${formatRelative(s.readiness[0]?.checkedAt)}${failed ? ` · ${failed} failing` : ""}` : undefined} flush>
      <DataTable
        rows={[...s.readiness].sort((a, b) => (a.status === "FAIL" ? -1 : b.status === "FAIL" ? 1 : 0))}
        rowKey={(c) => c.check}
        empty={<EmptyState title="Not tested yet" description="Readiness checks run at the end of provisioning and when you run Diagnostics." />}
        columns={[
          { key: "check", header: "Check", cell: (c) => <span className="font-mono text-[12.5px]">{c.check}</span> },
          { key: "status", header: "Result", cell: (c) => <StatusBadge status={c.status === "PASS" ? "SUCCESS" : c.status === "FAIL" ? "FAILED" : "CANCELLED"} /> },
          { key: "detail", header: "Detail", cell: (c) => <span className="text-[12.5px] text-muted">{c.detail}</span> },
        ]}
      />
    </Panel>
  );
}

function Events({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["server-events", id], queryFn: () => api.servers.events(id), refetchInterval: 15_000 });
  return (
    <Panel flush>
      <DataTable
        rows={q.data?.items}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        rowKey={(e) => e.id}
        empty={<EmptyState title="No events" />}
        columns={[
          { key: "t", header: "Time", cell: (e) => <span className="whitespace-nowrap text-muted tabular-nums">{formatDateTime(e.createdAt)}</span> },
          { key: "lvl", header: "Level", cell: (e) => <Badge tone={e.level === "ERROR" ? "danger" : e.level === "WARN" ? "warning" : "neutral"}>{e.level}</Badge> },
          { key: "m", header: "Event", cell: (e) => e.message },
        ]}
      />
    </Panel>
  );
}

function useCommand(serverId: string, commandId: string | null) {
  return useQuery({
    queryKey: ["command", serverId, commandId],
    queryFn: () => api.servers.commandStatus(serverId, commandId!),
    enabled: Boolean(commandId),
    refetchInterval: (query) => (["PENDING", "DISPATCHED"].includes(query.state.data?.status ?? "PENDING") ? 1500 : false),
  });
}

function CommandDialog({ serverId, commandId, title, onClose }: { serverId: string; commandId: string; title: string; onClose(): void }) {
  const q = useCommand(serverId, commandId);
  const cmd = q.data;
  const checks = (cmd?.result as { checks?: { check: string; status: string; detail: string }[] } | null)?.checks;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={title} size="lg" footer={<Button variant="primary" onClick={onClose}>Close</Button>}>
      <CommandBody cmd={cmd} error={q.error}>
        {checks && (
          <DataTable
            rows={checks}
            rowKey={(c) => c.check}
            columns={[
              { key: "c", header: "Check", cell: (c) => <span className="font-mono text-[12px]">{c.check}</span> },
              { key: "s", header: "Result", cell: (c) => <StatusBadge status={c.status === "PASS" ? "SUCCESS" : c.status === "FAIL" ? "FAILED" : "CANCELLED"} /> },
              { key: "d", header: "Detail", cell: (c) => <span className="text-[12px] text-muted">{c.detail}</span> },
            ]}
          />
        )}
      </CommandBody>
    </Dialog>
  );
}

function CommandBody({ cmd, error, children }: { cmd: AgentCommandRecord | undefined; error: unknown; children: React.ReactNode }) {
  if (error) return <ErrorState error={error} />;
  if (!cmd || cmd.status === "PENDING" || cmd.status === "DISPATCHED") {
    return <Spinner label={cmd?.status === "DISPATCHED" ? "Running on the server…" : "Waiting for the agent…"} />;
  }
  if (cmd.status !== "SUCCESS") return <Notice tone="danger" title={`Command ${cmd.status.toLowerCase()}`}>{cmd.error}</Notice>;
  return <>{children}</>;
}

function LogsDialog({ serverId, onClose }: { serverId: string; onClose(): void }) {
  const [form, setForm] = useState({ source: "agent", lines: "200", since: "1h", grep: "" });
  const [commandId, setCommandId] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.servers.command(serverId, { type: "LOGS", source: form.source as "agent", lines: Number(form.lines), since: form.since as "1h", ...(form.grep ? { grep: form.grep } : {}) }),
    onSuccess: (c) => setCommandId(c.id),
  });
  const q = useCommand(serverId, commandId);
  const lines = (q.data?.result as { lines?: string[] } | null)?.lines;
  const download = () => {
    const blob = new Blob([(lines ?? []).join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${form.source}-logs.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Server logs" size="lg" footer={<>{lines && <Button onClick={download}>Download</Button>}<Button variant="primary" onClick={onClose}>Close</Button></>}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Source">{(p) => (
            <Select {...p} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-36">
              <option value="agent">Agent</option><option value="docker">Docker</option><option value="proxy">Reverse proxy</option><option value="sshd">SSH</option><option value="system">System journal</option>
            </Select>
          )}</Field>
          <Field label="Since">{(p) => (
            <Select {...p} value={form.since} onChange={(e) => setForm({ ...form, since: e.target.value })} className="w-28">
              <option value="15m">15 min</option><option value="1h">1 hour</option><option value="6h">6 hours</option><option value="24h">24 hours</option>
            </Select>
          )}</Field>
          <Field label="Lines">{(p) => (
            <Select {...p} value={form.lines} onChange={(e) => setForm({ ...form, lines: e.target.value })} className="w-24">
              <option>100</option><option>200</option><option>500</option><option>2000</option>
            </Select>
          )}</Field>
          <Field label="Contains">{(p) => <Input {...p} value={form.grep} onChange={(e) => setForm({ ...form, grep: e.target.value })} className="w-40" />}</Field>
          <Button variant="primary" loading={send.isPending || (commandId !== null && ["PENDING", "DISPATCHED"].includes(q.data?.status ?? "PENDING"))} onClick={() => send.mutate()}>Fetch</Button>
        </div>
        {commandId && (
          <CommandBody cmd={q.data} error={q.error}>
            <pre className="max-h-96 overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-[11.5px] whitespace-pre-wrap">{lines?.length ? lines.join("\n") : "No matching log lines."}</pre>
          </CommandBody>
        )}
      </div>
    </Dialog>
  );
}
